import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { Footer } from "@/components/layout/Footer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatNumber } from "@/lib/utils";
import {
  useDailySalesClients,
  useDailySalesTransactions,
  useDailySalesTransactionTotals,
} from "@/hooks/settlement/se_dailySalesByClient/useDailySalesByClient";
import { DailySalesClient, DailySalesTransaction } from "@/interface/settlement/dailySalesByClient";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import { Code } from "@/interface/standard/st_code/index";
import { getToday } from "@/lib/date";

export default function DailySalesByClientPage() {
  const today = getToday();

  // 상단 검색 영역 (우측 패널용)
  const [company, setCompany] = useState("");
  const [itemSearchType, setItemSearchType] = useState("itemName");
  const [itemSearchValue, setItemSearchValue] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dateBtnValue, setDateBtnValue] = useState("today");

  // 좌측 패널 전용 검색 (매출일자 기준으로 필터링)
  const [leftSearchType, setLeftSearchType] = useState("companyName");
  const [leftSearchValue, setLeftSearchValue] = useState("");
  // 정확히 일치 체크 (모든 검색에 적용)
  const [exactMatch, setExactMatch] = useState(false);
  const [leftSearchParams, setLeftSearchParams] = useState<{
    searchType: string;
    search: string;
    exactMatch: boolean;
  }>({
    searchType: "",
    search: "",
    exactMatch: false,
  });

  // 좌측 패널: 날짜/회사/품목 변경 시 데이터 초기화 (첫 마운트 제외)
  const isFirstMountLeft = useRef(true);
  useEffect(() => {
    if (isFirstMountLeft.current) {
      isFirstMountLeft.current = false;
      return;
    }
    // 좌측 패널 데이터만 초기화 (selectedClientCode는 유지)
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
    setLeftLoadingMore(false);
  }, [startDate, endDate, company, itemSearchType, itemSearchValue]);

  // 우측 패널용 검색 파라미터 (날짜 변경 시 실시간 반영)
  const transactionParams = useMemo(() => ({
    startDate,
    endDate,
    company,
    itemSearchType,
    itemSearch: itemSearchValue,
    itemExactMatch: exactMatch,
  }), [startDate, endDate, company, itemSearchType, itemSearchValue, exactMatch]);

  // 선택된 거래처
  const [selectedClientCode, setSelectedClientCode] = useState<string>("");

  // 좌측 패널 페이지네이션 상태 (README.md 패턴)
  const [leftCurrentPage, setLeftCurrentPage] = useState(0);
  const [leftAllData, setLeftAllData] = useState<(DailySalesClient & { no: number })[]>([]);
  const [leftLoadingMore, setLeftLoadingMore] = useState(false);
  const [leftTotalElements, setLeftTotalElements] = useState(0);

  // 우측 패널 페이지네이션 상태 (README.md 패턴)
  const [rightCurrentPage, setRightCurrentPage] = useState(0);
  const [rightAllData, setRightAllData] = useState<(DailySalesTransaction & { no: number })[]>([]);
  const [rightLoadingMore, setRightLoadingMore] = useState(false);
  const [rightTotalElements, setRightTotalElements] = useState(0);

  // 회사 목록
  const { data: companyCodeList = [] } = useCodeListByCompanyCode();

  // 거래처 목록 API (좌측 패널 - Page 응답, 매출일자 기준)
  const { data: clientsData, isLoading: clientsLoading } = useDailySalesClients({
    startDate,
    endDate,
    company,
    itemSearchType,
    itemSearch: itemSearchValue,
    ...leftSearchParams,
    page: leftCurrentPage,
    size: 20,
  });

  // 거래 내역 API (우측 패널 - 거래처 선택 시에만 조회)
  const { data: transactionsData, isLoading: transactionsLoading } = useDailySalesTransactions(selectedClientCode, {
    ...transactionParams,
    page: rightCurrentPage,
    size: 20,
  });

  // 거래 합계 API (Footer용 - 전체 검색 결과 기준)
  const { data: totalsData } = useDailySalesTransactionTotals(selectedClientCode, transactionParams);

  // 좌측 패널: 초기 로딩만 수행, 조회 버튼과 무관

  // 좌측 패널: 데이터 누적 로직 (README.md 패턴)
  useEffect(() => {
    if (clientsData?.content) {
      setLeftAllData(prev => {
        // 첫 페이지면 초기화
        if (leftCurrentPage === 0) {
          setLeftTotalElements(clientsData.totalElements || 0);
          return clientsData.content.map((item, index) => ({
            no: index + 1,
            ...item,
          }));
        }
        // 다음 페이지면 중복 제거 후 추가
        const newData = clientsData.content.filter(
          newItem => !prev.some(existingItem => existingItem.companyCode === newItem.companyCode)
        );
        return [
          ...prev,
          ...newData.map((item, index) => ({
            no: prev.length + index + 1,
            ...item,
          })),
        ];
      });
    }
  }, [clientsData, leftCurrentPage]);

  // 좌측 패널: 로딩 상태 업데이트
  useEffect(() => {
    if (clientsData) {
      setLeftLoadingMore(false);
    }
  }, [clientsData]);

  // 좌측 패널: hasMore 계산
  const leftHasMore = useMemo(() => {
    if (!clientsData) return false;
    return !clientsData.last && leftAllData.length < leftTotalElements;
  }, [clientsData, leftAllData.length, leftTotalElements]);

  // 좌측 패널: 무한 스크롤 콜백
  const handleLeftLoadMore = useCallback(async () => {
    if (!leftHasMore || leftLoadingMore) return;
    setLeftLoadingMore(true);
    setLeftCurrentPage(prev => prev + 1);
  }, [leftHasMore, leftLoadingMore]);

  // 우측 패널용 첫 마운트 여부 추적
  const isFirstMountRight = useRef(true);

  // 우측 패널: 검색 조건 또는 거래처 변경 시 데이터 초기화 (첫 마운트 제외)
  useEffect(() => {
    if (isFirstMountRight.current) {
      isFirstMountRight.current = false;
      return;
    }
    setRightCurrentPage(0);
    setRightAllData([]);
    setRightTotalElements(0);
    setRightLoadingMore(false);
  }, [transactionParams, selectedClientCode]);

  // 우측 패널: 데이터 누적 로직 (README.md 패턴)
  useEffect(() => {
    if (transactionsData?.content) {
      setRightAllData(prev => {
        // 첫 페이지면 초기화
        if (rightCurrentPage === 0) {
          setRightTotalElements(transactionsData.totalElements || 0);
          return transactionsData.content.map((item, index) => ({
            no: index + 1,
            ...item,
          }));
        }
        // 다음 페이지면 중복 제거 후 추가
        const newData = transactionsData.content.filter(
          newItem => !prev.some(existingItem => existingItem.saleSlipCode === newItem.saleSlipCode)
        );
        return [
          ...prev,
          ...newData.map((item, index) => ({
            no: prev.length + index + 1,
            ...item,
          })),
        ];
      });
    }
  }, [transactionsData, rightCurrentPage]);

  // 우측 패널: 로딩 상태 업데이트
  useEffect(() => {
    if (transactionsData) {
      setRightLoadingMore(false);
    }
  }, [transactionsData]);

  // 우측 패널: hasMore 계산
  const rightHasMore = useMemo(() => {
    if (!transactionsData) return false;
    return !transactionsData.last && rightAllData.length < rightTotalElements;
  }, [transactionsData, rightAllData.length, rightTotalElements]);

  // 우측 패널: 무한 스크롤 콜백
  const handleRightLoadMore = useCallback(async () => {
    if (!rightHasMore || rightLoadingMore) return;
    setRightLoadingMore(true);
    setRightCurrentPage(prev => prev + 1);
  }, [rightHasMore, rightLoadingMore]);

  // 회사 옵션
  const companyOptions = useMemo(
    () => [
      { label: "전체", value: "" },
      ...companyCodeList.map((item: Code) => ({
        value: item.code || item.codeId?.toString() || "",
        label: item.name || "",
      })),
    ],
    [companyCodeList]
  );

  // 좌측 패널 전용 검색 옵션
  const leftSearchTypeOptions = [
    { label: "매출처", value: "companyName" },
    { label: "업체코드", value: "companyCode" },
  ];

  // 좌측 패널 검색 핸들러
  const handleLeftSearch = () => {
    setLeftSearchParams({
      searchType: leftSearchType,
      search: leftSearchValue,
      exactMatch,
    });
    // 데이터 초기화
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
    setSelectedClientCode("");
  };

  const itemSearchTypeOptions = [
    { label: "품목명", value: "itemName" },
    { label: "품목코드", value: "itemCode" },
  ];

  const handleReset = () => {
    const resetDate = getToday();
    setCompany("");
    setItemSearchType("itemName");
    setItemSearchValue("");
    setStartDate(resetDate);
    setEndDate(resetDate);
    setDateBtnValue("today");
    setSelectedClientCode("");
    // 좌측 패널 검색 초기화
    setLeftSearchType("companyName");
    setLeftSearchValue("");
    setExactMatch(false);
    setLeftSearchParams({
      searchType: "",
      search: "",
      exactMatch: false,
    });
    // 좌측 패널 데이터 초기화
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
    // 우측 패널: transactionParams는 useMemo로 자동 반영됨
  };

  // 좌측 거래처 목록 컬럼 (고정 너비)
  const clientColumns: TableColumn[] = [
    { key: "no", header: "번호", bodyAlign: "center", width: "50px", minWidth: "50px", maxWidth: "50px" },
    {
      key: "companyName",
      header: "매출처",
      bodyAlign: "left",
      width: "150px",
      minWidth: "150px",
      maxWidth: "150px",
      render: (v: string) => (
        <span className="block truncate max-w-[150px]" title={v}>
          {v}
        </span>
      ),
    },
    {
      key: "totalCount",
      header: "건수",
      bodyAlign: "center",
      width: "60px",
      minWidth: "60px",
      maxWidth: "60px",
      render: (v: number) => formatNumber(v ?? 0),
    },
  ];

  // 우측 거래 내역 컬럼
  const transactionColumns: TableColumn[] = [
    { key: "no", header: "번호", bodyAlign: "center", width: "60px" },
    { key: "saleDate", header: "매출일자", bodyAlign: "center", width: "110px" },
    { key: "itemCode", header: "품목코드", bodyAlign: "center", width: "100px" },
    { key: "itemName", header: "품목명", bodyAlign: "left", width: "150px" },
    { key: "qty", header: "매출수량", bodyAlign: "right", width: "80px", render: (v: number) => (v ?? 0).toFixed(2) },
    {
      key: "salesUnit",
      header: "운송단가",
      bodyAlign: "right",
      width: "90px",
      group: "매출액",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "salesAmt",
      header: "금액",
      bodyAlign: "right",
      width: "100px",
      group: "매출액",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "purchaseUnit",
      header: "운송단가",
      bodyAlign: "right",
      width: "90px",
      group: "매입액",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "purchaseAmt",
      header: "금액",
      bodyAlign: "right",
      width: "100px",
      group: "매입액",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "marginAmt",
      header: "금액",
      bodyAlign: "right",
      width: "100px",
      group: "운송마진",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "marginRate",
      header: "비율",
      bodyAlign: "right",
      width: "80px",
      group: "운송마진",
      render: (v: number) => `${(v ?? 0).toFixed(2)}%`,
    },
  ];

  // Footer 합계 (API에서 전체 검색 결과 기준으로 조회)
  const footerGrid = useMemo(() => {
    const totalQty = totalsData?.totalQty ?? 0;
    const totalSales = totalsData?.totalSalesAmt ?? 0;
    const totalPurchase = totalsData?.totalPurchaseAmt ?? 0;
    const totalMargin = totalsData?.totalMarginAmt ?? 0;
    const marginRate = totalSales > 0 ? (totalMargin / totalSales) * 100 : 0;

    return [
      [
        { label: "매출수량", value: totalQty.toFixed(2) },
        { label: "매출액", value: formatNumber(totalSales) },
        { label: "매입액", value: formatNumber(totalPurchase) },
        { label: "운송마진", value: formatNumber(totalMargin) },
        { label: "마진비율", value: `${marginRate.toFixed(2)}%` },
      ],
    ];
  }, [totalsData]);

  // 거래처 행 클릭 핸들러
  const handleClientRowClick = (row: DailySalesClient & { no: number }) => {
    setSelectedClientCode(row.companyCode);
  };

  return (
    <div className="flex flex-col h-full">
      {/* 검색영역 */}
      <SearchBoxLayout>
        <div className="flex items-end">
          {/* 좌측 패널 전용 검색 (매출처/업체코드) */}
          <SelectBox
            label="구분"
            className="w-28"
            value={leftSearchType}
            onChange={setLeftSearchType}
            options={leftSearchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={leftSearchValue}
            onChange={e => setLeftSearchValue(e.target.value)}
            placeholder="입력"
            onKeyDown={e => {
              if (e.key === "Enter") handleLeftSearch();
            }}
          />
          <div className="mr-1" />
          <Button variant="search" onClick={handleLeftSearch}>
            검색
          </Button>
          <div className="mr-3" />

          {/* 우측 패널용 검색 */}
          <SelectBox label="회사" className="w-40" value={company} onChange={setCompany} options={companyOptions} />
          <div className="mr-3" />
          <SelectBox
            label="구분"
            className="w-28"
            value={itemSearchType}
            onChange={setItemSearchType}
            options={itemSearchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={itemSearchValue}
            onChange={e => setItemSearchValue(e.target.value)}
            placeholder="입력"
          />
          <div className="mr-3" />

          <DateBox
            label="매출일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          />

          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* 상단 버튼 */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-end mb-2">
          <div className="flex gap-2">
            <PrintButton data={rightAllData} columns={transactionColumns} title="거래처별매출일보" skipPermissionCheck />
            <ExcelButton data={rightAllData} columns={transactionColumns} fileName="거래처별매출일보" skipPermissionCheck />
          </div>
        </div>

        {/* 본문 - 좌우 분할 */}
        <div className="flex-1 min-h-0 flex gap-4">
          {/* 좌측: 거래처 목록 */}
          <div className="w-72 flex-shrink-0">
            <Table
                className="h-full"
                columns={clientColumns}
                data={leftAllData}
                showRowNumber={false}
              maxHeight="100%"
              onRowClick={handleClientRowClick}
              highlightOnClick
              selectedRows={
                selectedClientCode
                  ? [leftAllData.findIndex(item => item.companyCode === selectedClientCode)].filter(i => i >= 0)
                  : []
              }
              loading={clientsLoading && leftAllData.length === 0}
              emptyMessage="조회 조건을 입력하고 조회 버튼을 클릭하세요"
              infiniteScroll={true}
              initialLoadCount={20}
              loadMoreCount={20}
              hasMore={leftHasMore}
              onLoadMore={handleLeftLoadMore}
              loadingMore={leftLoadingMore}
              enablePagination={true}
              pageSize={20}
              onPageChange={setLeftCurrentPage}
              currentPage={leftCurrentPage}
            />
          </div>

          {/* 우측: 거래 내역 */}
          <div className="flex-1 min-w-0">
            <Table
              className="h-full"
              columns={transactionColumns}
              data={rightAllData}
              showRowNumber={false}
              resizable
              maxHeight="100%"
              loading={transactionsLoading && rightAllData.length === 0}
              emptyMessage="거래처를 선택하세요"
              infiniteScroll={true}
              initialLoadCount={20}
              loadMoreCount={20}
              hasMore={rightHasMore}
              onLoadMore={handleRightLoadMore}
              loadingMore={rightLoadingMore}
              enablePagination={true}
              pageSize={20}
              onPageChange={setRightCurrentPage}
              currentPage={rightCurrentPage}
            />
          </div>
        </div>
      </main>

      {/* 푸터 총계 */}
      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>
    </div>
  );
}
